import type { ColorSystem } from '@mui/material/styles';

import { createPaletteChannel, hexToRgbChannel, setFont } from 'minimal-shared/utils';

import type { SettingsState } from '../../components/settings/types';
import { createShadowColor } from '../core/custom-shadows';
import type { ThemeColorScheme, ThemeOptions } from '../types';
import { primaryColorPresets } from './color-presets';

/**
 * Update the core theme with the settings state.
 * @contrast
 * @primaryColor
 */
export function updateCoreWithSettings(
    theme: ThemeOptions,
    settingsState?: SettingsState,
): ThemeOptions {
    const {
        direction,
        fontFamily,
        contrast = 'default',
        primaryColor = 'default',
    } = settingsState ?? {};

    const isDefaultContrast = contrast === 'default';
    const isDefaultPrimaryColor = primaryColor === 'default';

    const lightPalette = theme.colorSchemes?.light.palette as ColorSystem['palette'];

    const updatedPrimaryColor = createPaletteChannel(primaryColorPresets[primaryColor]);
    // const updatedSecondaryColor = createPaletteChannel(SECONDARY_COLORS[primaryColor!]);

    /**
     * Updates a theme's color scheme with specified settings.
     *
     * @param {ThemeColorScheme} scheme - The color scheme to update ('light' or 'dark')
     * @returns {object} The updated color scheme with modified palette and custom shadows
     *
     * @remarks
     * This function applies several customizations to a theme:
     * - Updates the primary color when it differs from default
     * - For light mode specifically, adjusts the background color based on contrast settings
     * - Updates custom shadows to match the modified primary color
     */
    const updateColorScheme = (scheme: ThemeColorScheme) => {
        const colorSchemes = theme.colorSchemes?.[scheme];

        const updatedPalette = {
            ...colorSchemes?.palette,
            ...(!isDefaultPrimaryColor && {
                primary: updatedPrimaryColor,
                // secondary: updatedSecondaryColor,
            }),
            ...(scheme === 'light' && {
                background: {
                    ...lightPalette?.background,
                    ...(!isDefaultContrast && {
                        default: lightPalette.grey[200],
                        defaultChannel: hexToRgbChannel(lightPalette.grey[200]),
                    }),
                },
            }),
        };

        const updatedCustomShadows = {
            ...colorSchemes?.customShadows,
            ...(!isDefaultPrimaryColor && {
                primary: createShadowColor(updatedPrimaryColor.mainChannel),
                // secondary: createShadowColor(updatedSecondaryColor.mainChannel),
            }),
        };

        return {
            ...colorSchemes,
            palette: updatedPalette,
            customShadows: updatedCustomShadows,
        };
    };

    return {
        ...theme,
        direction,
        colorSchemes: {
            light: updateColorScheme('light'),
            dark: updateColorScheme('dark'),
        },
        typography: {
            ...theme.typography,
            fontFamily: setFont(fontFamily),
        },
    };
}
